import { AccountingTz } from '~/utils/timezone'

// Cached formatters keyed by tz — Intl.DateTimeFormat construction is the
// hot cost in a large table; reuse instances per tz across calls.
const fullFormatters = new Map<string, Intl.DateTimeFormat>()
const yearFormatters = new Map<string, Intl.DateTimeFormat>()

const getFullFormatter = (timeZone: string) => {
  const cached = fullFormatters.get(timeZone)
  if (cached) return cached
  const f = new Intl.DateTimeFormat('en-US', {
    timeZone,
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  })
  fullFormatters.set(timeZone, f)
  return f
}

const getYearFormatter = (timeZone: string) => {
  const cached = yearFormatters.get(timeZone)
  if (cached) return cached
  const f = new Intl.DateTimeFormat('en-US', { timeZone, year: 'numeric' })
  yearFormatters.set(timeZone, f)
  return f
}

/**
 * Formats a timestamp as "DD MMM" or "DD MMM YYYY", in the given tz.
 *
 * - `abbreviate=true` (default): omits year for same-year dates ("13 Mar"),
 *   includes year for different-year dates ("15 Jun 2023").
 * - `abbreviate=false`: always includes year ("13 Mar 2024").
 */
export const absoluteTimeDiff = (
  ms: number | string,
  options?: { abbreviate?: boolean; tz?: AccountingTz },
) => {
  const date = new Date(ms)
  if (Number.isNaN(date.getTime())) return ''

  const { abbreviate = true, tz = 'local' } = options ?? {}
  const timeZone =
    tz === 'utc'
      ? 'UTC'
      : Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC'

  const parts = getFullFormatter(timeZone).formatToParts(date)
  const day = parts.find((p) => p.type === 'day')?.value ?? ''
  const month = parts.find((p) => p.type === 'month')?.value ?? ''
  const year = parts.find((p) => p.type === 'year')?.value ?? ''

  const currentYear = getYearFormatter(timeZone)
    .formatToParts(new Date())
    .find((p) => p.type === 'year')?.value

  if (abbreviate && year === currentYear) return `${day} ${month}`
  return `${day} ${month} ${year}`
}
